import * as Sentry from "@sentry/tanstackstart-react";
import { rootRouteId, useMatch, useRouter } from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { RefreshCw, TriangleAlert } from "lucide-react";
import { useState } from "react";

import { Button, ButtonRouter } from "~/components/ui/button";
import { EmptyState } from "~/components/ui/empty-state";
import { isChunkLoadError } from "~/lib/shared/chunk-load-error";

/**
 * Shown when part of the page's code failed to load. TanStack Router's lazy
 * route components have already reloaded once by the time this renders, so we
 * hand the next reload to the visitor instead of looping.
 */
function RefreshNeeded() {
  const [refreshing, setRefreshing] = useState(false);

  return (
    <EmptyState
      variant="hero"
      size="lg"
      icon={RefreshCw}
      title="This page needs a quick refresh"
      description="We may have just released an update, or the connection dropped for a moment. Refreshing loads the latest version."
    >
      <Button
        variant="primary"
        loading={refreshing}
        onClick={() => {
          setRefreshing(true);
          window.location.reload();
        }}
      >
        Refresh page
      </Button>
    </EmptyState>
  );
}

function SomethingWentWrong({ error }: Pick<ErrorComponentProps, "error">) {
  const router = useRouter();
  const isRoot = useMatch({
    strict: false,
    select: (state) => state.id === rootRouteId,
  });

  return (
    <EmptyState
      size="lg"
      icon={TriangleAlert}
      title="Something went wrong"
      description="We couldn't show this page. Try again, or head back to where you were."
    >
      <Button
        variant="primary"
        onClick={() => {
          void router.invalidate();
        }}
      >
        Try again
      </Button>
      {isRoot ? (
        <ButtonRouter to="/">Home</ButtonRouter>
      ) : (
        <Button onClick={() => window.history.back()}>Go back</Button>
      )}
      {import.meta.env.DEV && (
        <pre className="mt-4 w-full max-w-md overflow-auto rounded-lg bg-gray-50 p-3 text-left text-xs text-red-700 ring-1 ring-gray-200">
          {error instanceof Error ? error.message : String(error)}
        </pre>
      )}
    </EmptyState>
  );
}

function DefaultCatchBoundaryInternal({ error }: ErrorComponentProps) {
  return (
    <div className="flex min-w-0 flex-1 flex-col items-center justify-center p-4">
      {isChunkLoadError(error) ? (
        <RefreshNeeded />
      ) : (
        <SomethingWentWrong error={error} />
      )}
    </div>
  );
}

export const DefaultCatchBoundary = Sentry.withErrorBoundary(
  DefaultCatchBoundaryInternal,
  {},
);
